import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabaseClient';
import { auth } from '../../lib/firebase';
import {
  Megaphone,
  X,
  Loader,
  CheckCircle,
  AlertTriangle,
  Wifi,
  Rocket,
  Crown,
  Gem,
  Sparkles,
  Wallet,
  Clock,
  TrendingUp,
  Eye,
  RefreshCw,
  Info,
  Zap,
  BadgeCheck,
  XCircle,
  Timer,
  ListChecks,
} from 'lucide-react';
import './Ads.css';

/* ==================================================================
   TYPES
   ================================================================== */

export type AdStatus = 'pending' | 'running' | 'expired' | 'failed';

export interface AdPlan {
  id: string;
  name: string;
  amount: number;
  duration_days: number;
  tagline: string;
  description: string;
  badge?: string;
  color: string;
  icon: React.ReactNode;
}

export interface PromoteTargetMeta {
  /** e.g. 'business_deal', 'product', 'vendor', 'service' */
  type: string;
  /** id of the target being promoted */
  id: string;
  /** display name (business, product title, etc.) */
  name: string;
  /** optional image url */
  image?: string;
  /** optional category / table being promoted */
  category?: string;
  /** any extra JSON you want stored alongside the ad */
  extra?: Record<string, any>;
}

interface BusinessAd {
  id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  target_type: string;
  target_id: string;
  target_name?: string;
  target_image?: string;
  target_category?: string;
  plan_id: string;
  plan_name: string;
  amount: number;
  duration_days: number;
  status: AdStatus;
  payment_reference?: string;
  failure_reason?: string;
  started_at?: string;
  expires_at?: string;
  created_at: string;
  updated_at?: string;
  extra?: Record<string, any>;
}

/* ==================================================================
   PLAN CATALOG
   ================================================================== */

export const AD_PLANS: AdPlan[] = [
  {
    id: 'startup',
    name: 'Startup',
    amount: 2500,
    duration_days: 30,
    tagline: 'Starter Ads',
    description: 'Get your business in front of new eyes with starter ads for 1 month.',
    badge: 'Starter',
    color: '#3b82f6',
    icon: <Rocket size={18} />,
  },
  {
    id: 'advanced',
    name: 'Advanced',
    amount: 25000,
    duration_days: 30,
    tagline: 'Advanced Ads',
    description: 'Advanced ads for your business for 1 month — higher reach, higher impact.',
    badge: 'Popular',
    color: '#9B4819',
    icon: <Crown size={18} />,
  },
  {
    id: 'premium',
    name: 'Premium',
    amount: 100000,
    duration_days: 30,
    tagline: 'Premium Ads',
    description: 'Premium ads for your business for 1 month — maximum visibility & priority placement.',
    badge: 'Best Value',
    color: '#7c3aed',
    icon: <Gem size={18} />,
  },
  {
    id: 'custom',
    name: 'Custom',
    amount: 0,
    duration_days: 30,
    tagline: 'Set Your Budget',
    description: 'Set a custom budget for your business ads for 1 month.',
    badge: 'Flexible',
    color: '#10b981',
    icon: <Sparkles size={18} />,
  },
];

const TABLE = 'business_ads';

/* ==================================================================
   HELPERS
   ================================================================== */

const formatCurrency = (amount: number, currency = 'NGN'): string => {
  try {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `₦${amount.toLocaleString()}`;
  }
};

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const daysLeft = (expiresAt?: string): number => {
  if (!expiresAt) return 0;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

const isNetworkError = (err: any): boolean => {
  const msg = String(err?.message || err?.error_description || err || '').toLowerCase();
  return (
    msg.includes('network') ||
    msg.includes('failed to fetch') ||
    msg.includes('fetch failed') ||
    msg.includes('timeout') ||
    msg.includes('offline')
  );
};

const isQuotaOrSubscriptionError = (err: any): boolean => {
  const msg = String(err?.message || '').toLowerCase();
  const code = String(err?.code || '');
  return (
    code === 'PGRST301' ||
    code === 'PGRST302' ||
    msg.includes('quota') ||
    msg.includes('subscription') ||
    msg.includes('payment required') ||
    msg.includes('exceeded') ||
    msg.includes('limit reached')
  );
};

const friendlyError = (err: any): string => {
  if (isNetworkError(err)) return 'No network. Please check your connection and try again.';
  if (isQuotaOrSubscriptionError(err))
    return 'Service temporarily unavailable. Please check back later.';
  if (err?.code === '42P01') return 'Ads service is being set up. Please try again shortly.';
  return 'Something went wrong. Please try again.';
};

const isTableMissing = (err: any): boolean => {
  const msg = String(err?.message || '').toLowerCase();
  return err?.code === '42P01' || msg.includes('does not exist') || msg.includes('relation');
};

/* ==================================================================
   STATUS SYNC  (running → expired after 30 days)
   ================================================================== */

const syncAdStatuses = async (): Promise<void> => {
  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select('id, status, expires_at')
      .in('status', ['running', 'pending']);

    if (error) {
      if (isTableMissing(error)) return;
      return;
    }

    const now = Date.now();
    const expiredIds: string[] = [];

    (data || []).forEach((row: any) => {
      if (row.expires_at && new Date(row.expires_at).getTime() < now) {
        expiredIds.push(row.id);
      }
    });

    if (expiredIds.length > 0) {
      await supabase
        .from(TABLE)
        .update({ status: 'expired', updated_at: new Date().toISOString() })
        .in('id', expiredIds);
    }
  } catch {
    // silent
  }
};

/* ==================================================================
   MAIN HOOK: useAds
   ================================================================== */

export const useAds = () => {
  const [ads, setAds] = useState<BusinessAd[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAds = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setAds([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await syncAdStatuses();

      const { data, error: fetchErr } = await supabase
        .from(TABLE)
        .select('*')
        .eq('user_id', user.uid)
        .order('created_at', { ascending: false });

      if (fetchErr) {
        if (isTableMissing(fetchErr)) {
          setAds([]);
          setError(null);
        } else {
          setError(friendlyError(fetchErr));
        }
        return;
      }

      setAds((data || []) as BusinessAd[]);
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  return { ads, loading, error, refresh: fetchAds };
};

/* ==================================================================
   CORE: createAd
   ================================================================== */

interface CreateAdParams {
  planId: string;
  customAmount?: number;
  target: PromoteTargetMeta;
}

interface CreateAdResult {
  success: boolean;
  error?: string;
  insufficientBalance?: boolean;
  adId?: string;
}

const createAd = async (params: CreateAdParams): Promise<CreateAdResult> => {
  const user = auth.currentUser;
  if (!user) return { success: false, error: 'You must be signed in.' };

  const plan = AD_PLANS.find((p) => p.id === params.planId);
  if (!plan) return { success: false, error: 'Invalid plan.' };

  const amount = plan.id === 'custom' ? Number(params.customAmount) || 0 : plan.amount;
  if (amount <= 0) {
    return { success: false, error: 'Please enter a valid amount.' };
  }

  const nowIso = new Date().toISOString();

  try {
    const { data: userRow, error: userErr } = await supabase
      .from('users')
      .select('id, balance, name, email')
      .eq('firebase_uid', user.uid)
      .maybeSingle();

    if (userErr) {
      if (isTableMissing(userErr))
        return { success: false, error: 'Account service unavailable. Try again later.' };
      return { success: false, error: friendlyError(userErr) };
    }

    const currentBalance = Number(userRow?.balance) || 0;
    const userName = userRow?.name || user.displayName || 'User';
    const userEmail = userRow?.email || user.email || '';

    if (currentBalance < amount) {
      try {
        await supabase.from(TABLE).insert([
          {
            user_id: user.uid,
            user_name: userName,
            user_email: userEmail,
            target_type: params.target.type,
            target_id: params.target.id,
            target_name: params.target.name,
            target_image: params.target.image || null,
            target_category: params.target.category || null,
            plan_id: plan.id,
            plan_name: plan.name,
            amount,
            duration_days: plan.duration_days,
            status: 'failed',
            failure_reason: 'Insufficient balance',
            created_at: nowIso,
            updated_at: nowIso,
            extra: params.target.extra || null,
          },
        ]);
      } catch {
        // non-critical
      }

      return {
        success: false,
        insufficientBalance: true,
        error: 'Insufficient balance. Redirecting to deposit…',
      };
    }

    const newBalance = currentBalance - amount;

    const { error: deductErr } = await supabase
      .from('users')
      .update({ balance: newBalance, updated_at: nowIso })
      .eq('firebase_uid', user.uid);

    if (deductErr) {
      return { success: false, error: friendlyError(deductErr) };
    }

    const expiresAt = new Date(Date.now() + plan.duration_days * 86400000).toISOString();

    const { data: inserted, error: insertErr } = await supabase
      .from(TABLE)
      .insert([
        {
          user_id: user.uid,
          user_name: userName,
          user_email: userEmail,
          target_type: params.target.type,
          target_id: params.target.id,
          target_name: params.target.name,
          target_image: params.target.image || null,
          target_category: params.target.category || null,
          plan_id: plan.id,
          plan_name: plan.name,
          amount,
          duration_days: plan.duration_days,
          status: 'running',
          started_at: nowIso,
          expires_at: expiresAt,
          created_at: nowIso,
          updated_at: nowIso,
          extra: params.target.extra || null,
        },
      ])
      .select()
      .single();

    if (insertErr) {
      await supabase
        .from('users')
        .update({ balance: currentBalance, updated_at: nowIso })
        .eq('firebase_uid', user.uid);

      if (isTableMissing(insertErr)) {
        return { success: false, error: 'Ads service is being set up. Please try again shortly.' };
      }
      return { success: false, error: friendlyError(insertErr) };
    }

    try {
      await supabase.from('transactions').insert({
        user_id: user.uid,
        user_name: userName,
        vendor_id: null,
        vendor_name: 'Ads',
        transaction_type: 'ads_payment',
        amount,
        description: `${plan.name} Ads for ${params.target.name}`,
        user_status_at_time: 'paid',
        vendor_status_at_time: 'n/a',
        order_created_at: nowIso,
        transaction_created_at: nowIso,
      });
    } catch {
      // non-critical
    }

    return { success: true, adId: inserted?.id };
  } catch (err: any) {
    return { success: false, error: friendlyError(err) };
  }
};

/* ==================================================================
   PORTAL HELPER
   Renders children into document.body so modals escape ALL parent
   stacking contexts (overflow, transform, z-index) — exactly like a
   top-level modal should.
   ================================================================== */

const AdsPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted || typeof document === 'undefined') return null;
  return createPortal(children, document.body);
};

/* ==================================================================
   COMPONENT: AdsPromoteButton
   ================================================================== */

interface AdsPromoteButtonProps {
  target: PromoteTargetMeta;
  label?: string;
  variant?: 'icon' | 'icon-label' | 'full';
  className?: string;
  onSuccess?: (adId?: string) => void;
}

export const AdsPromoteButton: React.FC<AdsPromoteButtonProps> = ({
  target,
  label = 'Boost',
  variant = 'icon-label',
  className = '',
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const navigate = (path: string) => {
    try {
      window.location.href = path;
    } catch {}
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const handleSuccess = (adId?: string) => {
    setOpen(false);
    onSuccess?.(adId);
  };

  const handleInsufficient = () => {
    setOpen(false);
    setTimeout(() => navigate('/money'), 600);
  };

  return (
    <>
      <button
        type="button"
        className={`ads-promote-btn ads-promote-btn--${variant} ${className}`}
        onClick={handleOpen}
        title={label}
        aria-label={label}
      >
        <Megaphone size={variant === 'icon' ? 16 : 13} />
        {variant !== 'icon' && <span>{label}</span>}
      </button>

      {open && (
        <AdsPortal>
          <AdsPlansModal
            target={target}
            onClose={() => setOpen(false)}
            onSuccess={handleSuccess}
            onInsufficient={handleInsufficient}
          />
        </AdsPortal>
      )}
    </>
  );
};

/* ==================================================================
   COMPONENT: AdsPlansModal
   ================================================================== */

interface AdsPlansModalProps {
  target: PromoteTargetMeta;
  onClose: () => void;
  onSuccess: (adId?: string) => void;
  onInsufficient: () => void;
}

const AdsPlansModal: React.FC<AdsPlansModalProps> = ({
  target,
  onClose,
  onSuccess,
  onInsufficient,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<string>('advanced');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const selected = useMemo(
    () => AD_PLANS.find((p) => p.id === selectedPlan),
    [selectedPlan]
  );

  const amountToPay =
    selectedPlan === 'custom' ? Number(customAmount) || 0 : selected?.amount || 0;

  const handlePromote = async () => {
    if (selectedPlan === 'custom' && amountToPay <= 0) {
      setErrorMsg('Enter a valid custom amount.');
      return;
    }
    setProcessing(true);
    setErrorMsg(null);

    const res = await createAd({
      planId: selectedPlan,
      customAmount: amountToPay,
      target,
    });

    setProcessing(false);

    if (res.success) {
      setSuccessMsg('Ads launched successfully!');
      setTimeout(() => onSuccess(res.adId), 900);
      return;
    }

    if (res.insufficientBalance) {
      setErrorMsg('Insufficient balance. Redirecting to deposit…');
      setTimeout(() => onInsufficient(), 1400);
      return;
    }

    setErrorMsg(res.error || 'Could not launch ads.');
  };

  return (
    <div className="ads-modal-overlay" onClick={onClose}>
      <div className="ads-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ads-modal-header">
          <div className="ads-modal-header-title">
            <Megaphone size={16} />
            <h3>Promote Your Business</h3>
          </div>
          <button className="ads-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="ads-modal-body">
          <div className="ads-target-preview">
            {target.image ? (
              <img
                src={target.image}
                alt={target.name}
                className="ads-target-img"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="ads-target-img-placeholder">
                {(target.name || 'A').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="ads-target-info">
              <div className="ads-target-label">Promoting</div>
              <div className="ads-target-name">{target.name}</div>
              {target.category && (
                <div className="ads-target-category">{target.category}</div>
              )}
            </div>
          </div>

          <div className="ads-plans-grid">
            {AD_PLANS.map((plan) => {
              const isSelected = selectedPlan === plan.id;
              return (
                <button
                  key={plan.id}
                  type="button"
                  className={`ads-plan-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan(plan.id)}
                  style={{ borderColor: isSelected ? plan.color : undefined }}
                >
                  {plan.badge && (
                    <span className="ads-plan-badge" style={{ background: plan.color }}>
                      {plan.badge}
                    </span>
                  )}
                  <div className="ads-plan-icon" style={{ color: plan.color }}>
                    {plan.icon}
                  </div>
                  <div className="ads-plan-name">{plan.name}</div>
                  <div className="ads-plan-price">
                    {plan.id === 'custom'
                      ? 'Custom'
                      : formatCurrency(plan.amount)}
                  </div>
                  <div className="ads-plan-duration">1 month</div>
                  <div className="ads-plan-desc">{plan.description}</div>
                  {isSelected && (
                    <div className="ads-plan-selected-mark" style={{ background: plan.color }}>
                      <CheckCircle size={12} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {selectedPlan === 'custom' && (
            <div className="ads-custom-field">
              <label>Enter custom budget (₦)</label>
              <input
                type="number"
                min={100}
                step={100}
                placeholder="e.g. 5000"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
              />
            </div>
          )}

          <div className="ads-summary">
            <div className="ads-summary-row">
              <span>Plan</span>
              <span>{selected?.name}</span>
            </div>
            <div className="ads-summary-row">
              <span>Duration</span>
              <span>30 days</span>
            </div>
            <div className="ads-summary-row ads-summary-total">
              <span>Total</span>
              <span>{formatCurrency(amountToPay)}</span>
            </div>
          </div>

          {errorMsg && (
            <div className="ads-alert ads-alert-error">
              <AlertTriangle size={12} />
              <span>{errorMsg}</span>
            </div>
          )}
          {successMsg && (
            <div className="ads-alert ads-alert-success">
              <CheckCircle size={12} />
              <span>{successMsg}</span>
            </div>
          )}
        </div>

        <div className="ads-modal-footer">
          <button className="ads-btn ads-btn-secondary" onClick={onClose} disabled={processing}>
            Cancel
          </button>
          <button
            className="ads-btn ads-btn-primary"
            onClick={handlePromote}
            disabled={processing}
          >
            {processing ? (
              <>
                <Loader size={13} className="ads-spin" />
                Processing…
              </>
            ) : (
              <>
                <Zap size={13} />
                Promote
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ==================================================================
   COMPONENT: MyAdsButton
   ================================================================== */

interface MyAdsButtonProps {
  variant?: 'icon' | 'icon-label' | 'full';
  label?: string;
  className?: string;
}

export const MyAdsButton: React.FC<MyAdsButtonProps> = ({
  variant = 'icon-label',
  label = 'My Ads',
  className = '',
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className={`ads-myads-btn ads-myads-btn--${variant} ${className}`}
        onClick={handleOpen}
        title={label}
        aria-label={label}
      >
        <ListChecks size={variant === 'icon' ? 16 : 13} />
        {variant !== 'icon' && <span>{label}</span>}
      </button>

      {open && (
        <AdsPortal>
          <MyAdsModal onClose={() => setOpen(false)} />
        </AdsPortal>
      )}
    </>
  );
};

/* ==================================================================
   COMPONENT: MyAdsModal
   ================================================================== */

interface MyAdsModalProps {
  onClose: () => void;
}

export const MyAdsModal: React.FC<MyAdsModalProps> = ({ onClose }) => {
  const { ads, loading, error, refresh } = useAds();

  const stats = useMemo(() => {
    const running = ads.filter((a) => a.status === 'running').length;
    const expired = ads.filter((a) => a.status === 'expired').length;
    const failed = ads.filter((a) => a.status === 'failed').length;
    const totalSpent = ads
      .filter((a) => a.status === 'running' || a.status === 'expired')
      .reduce((s, a) => s + (Number(a.amount) || 0), 0);
    return { running, expired, failed, totalSpent, total: ads.length };
  }, [ads]);

  return (
    <div className="ads-modal-overlay" onClick={onClose}>
      <div className="ads-modal ads-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="ads-modal-header">
          <div className="ads-modal-header-title">
            <ListChecks size={16} />
            <h3>My Ads</h3>
            <span className="ads-header-count">{stats.total}</span>
          </div>
          <div className="ads-modal-header-actions">
            <button
              className="ads-icon-btn"
              onClick={refresh}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'ads-spin' : ''} />
            </button>
            <button className="ads-modal-close" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="ads-modal-body">
          <div className="ads-stats-strip">
            <div className="ads-stat-pill ads-stat-running">
              <TrendingUp size={11} />
              <span>{stats.running} Running</span>
            </div>
            <div className="ads-stat-pill ads-stat-expired">
              <Timer size={11} />
              <span>{stats.expired} Expired</span>
            </div>
            <div className="ads-stat-pill ads-stat-failed">
              <XCircle size={11} />
              <span>{stats.failed} Failed</span>
            </div>
            <div className="ads-stat-pill ads-stat-spent">
              <Wallet size={11} />
              <span>{formatCurrency(stats.totalSpent)} Value</span>
            </div>
          </div>

          {loading && ads.length === 0 ? (
            <div className="ads-state">
              <Loader size={26} className="ads-spin" />
              <p>Loading your ads…</p>
            </div>
          ) : error ? (
            <div className="ads-state ads-state-error">
              <Wifi size={28} />
              <p>{error}</p>
              <button className="ads-btn ads-btn-secondary" onClick={refresh}>
                <RefreshCw size={12} />
                Retry
              </button>
            </div>
          ) : ads.length === 0 ? (
            <div className="ads-state">
              <Megaphone size={32} />
              <h4>No ads yet</h4>
              <p>Promote your business, products or services to see them here.</p>
            </div>
          ) : (
            <div className="ads-list">
              {ads.map((ad) => (
                <AdRow key={ad.id} ad={ad} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ==================================================================
   AdRow
   ================================================================== */

const AdRow: React.FC<{ ad: BusinessAd }> = ({ ad }) => {
  const statusConfig: Record<
    AdStatus,
    { label: string; color: string; bg: string; icon: React.ReactNode }
  > = {
    running: {
      label: 'Running',
      color: '#10b981',
      bg: 'rgba(16,185,129,0.1)',
      icon: <TrendingUp size={10} />,
    },
    pending: {
      label: 'Pending',
      color: '#f59e0b',
      bg: 'rgba(245,158,11,0.1)',
      icon: <Clock size={10} />,
    },
    expired: {
      label: 'Expired',
      color: '#6b7280',
      bg: 'rgba(107,114,128,0.1)',
      icon: <Timer size={10} />,
    },
    failed: {
      label: 'Failed',
      color: '#ef4444',
      bg: 'rgba(239,68,68,0.1)',
      icon: <XCircle size={10} />,
    },
  };

  const conf = statusConfig[ad.status] || statusConfig.failed;
  const remaining = ad.status === 'running' ? daysLeft(ad.expires_at) : 0;

  return (
    <div className={`ads-row ads-row-${ad.status}`}>
      <div className="ads-row-media">
        {ad.target_image ? (
          <img
            src={ad.target_image}
            alt={ad.target_name}
            referrerPolicy="no-referrer"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="ads-row-media-placeholder">
            {(ad.target_name || 'A').charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      <div className="ads-row-info">
        <div className="ads-row-title">
          <span className="ads-row-name">{ad.target_name || 'Untitled'}</span>
          <span className="ads-row-status" style={{ color: conf.color, background: conf.bg }}>
            {conf.icon}
            {conf.label}
          </span>
        </div>
        <div className="ads-row-meta">
          <span className="ads-plan-tag">
            <BadgeCheck size={10} />
            {ad.plan_name}
          </span>
          <span className="ads-row-amount">{formatCurrency(Number(ad.amount) || 0)}</span>
        </div>
        <div className="ads-row-dates">
          <span>
            <Clock size={9} />
            Started: {formatDate(ad.started_at || ad.created_at)}
          </span>
          {ad.status === 'running' && ad.expires_at && (
            <span className="ads-row-remaining">
              <Timer size={9} />
              {remaining} day{remaining !== 1 ? 's' : ''} left
            </span>
          )}
          {ad.status === 'expired' && ad.expires_at && (
            <span className="ads-row-expired">
              <Info size={9} />
              Ended: {formatDate(ad.expires_at)}
            </span>
          )}
        </div>
        {ad.status === 'failed' && ad.failure_reason && (
          <div className="ads-row-failure">
            <AlertTriangle size={10} />
            {ad.failure_reason}
          </div>
        )}
      </div>
    </div>
  );
};

/* ==================================================================
   Convenience — direct modal exports
   ================================================================== */

export { AdsPlansModal, MyAdsModal as MyAdsModalExport };
export default AdsPromoteButton;